/* eslint-disable no-param-reassign */
import { RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { partition } from 'lodash';
import { enqueueSnackbar, type VariantType } from 'notistack';
import { createPortal } from 'react-dom';
import useAiReviewerRole from 'common/hooks/useAiReviewerRole';
import useCanClearCapTable from 'common/hooks/useCanClearCapTable';
import { CellChange } from 'components/Spreadsheet/Spreadsheet.types';
import { IWorkbook } from 'models/interfaces/spreadsheet-types/workbook-types';
import AiProcessingMenu from './AiProcessingMenu';
import GenerateCapTableJob from './GenerateCapTableJob';
import {
  AcceptAIFieldRequest,
  AIEvidenceResetRequest,
  ApiCreateDocumentGenerationJob,
  ApiService,
  CapTablesService,
  CompanyService,
  DocumentReferencesView,
  SecurityModel,
} from '../../../../../api';
import { SECURITY_NAME_ALIAS } from '../../../../../common/constants/cap-table/capTable';
import { useStore } from '../../../../../common/store';
import { UseStoreValues } from '../../../../../common/types/store';
import { HandlePushingAttributeMappingsPayload } from '../../../../../components/Drawer/attribute-mapped-widget-drawer/attribute-mapped-widget-drawer.types';
import { AttributeMappedWidgetDrawer } from '../../../../../components/Drawer/attribute-mapped-widget-drawer/AttributeMappedWidgetDrawer';
import { CitationPushedPayload } from '../../../../../components/Drawer/attribute-mapped-widget-drawer/AttributeMappedWidgetDrawerContext';
import WidgetDrawerProps from '../../../../../components/Drawer/widget-drawer/WidgetDrawer.props';
import { EventType, useEventBus, useEventSubscription } from '../../../../../context';
import { useAsyncProcessor } from '../../../../../context/AsyncProcessorContext/AsyncProcessorContext';
import { DocumentReference, useDocumentFootnotes } from '../../../../../context/DocumentFootnotesContext';
import { NavigateToDocumentReferencePayload } from '../../../../../context/EventBusContext/EventBusContext.type';
import { useLayoutContext } from '../../../../../context/LayoutContext';
import UnsavedChangesContext from '../../../../../context/UnsavedChangesContext';
import type { UnsavedChangesType } from '../../../../../context/UnsavedChangesContext/types';
import CapatableService, {
  type ClearEvidenceResult,
  type ReviewBatchResponse,
} from '../../../../../services/captable';
import { createCapTableGenerationOnResult } from '../../../../../services/capTableGenerationResult';
import { CAP_TABLE_REPROCESS_JOB_TITLE } from '../../../../../services/generationJobs';
import { useGetCaptableInfo, usePatchCapTableSecurities } from '../../../../../services/hooks/captable';
import { useGetDocumentsById } from '../../../../../services/hooks/documents';
import { CapTableReferenceGenerator, MappingReferenceData } from '../../../../../services/reference-generator';
import { ClearAttributeMappingsPayload } from '../../../../../services/sheet-attribute-manager/models/sheet-attribute-manager.interface';
import { useCellMappingStore, usePendingReviewCount, useReviewPaneCollapsed, useReviewStore } from '../../../../../store';
import { useAttributeMapperStore } from '../../../../../store/useAttributeMapperStore';
import { useCapTableDirtyCellStore } from '../../../../../store/useCapTableDirtyCellStore';
import type { ReviewItem, ReviewStatus } from '../../../../../store/useReviewStore';
import { extractResponseErrorMessage, Logger } from '../../../../../utilities';
import { AttributeMappingType } from '../../../../Financials/financials-widget-drawer/types';
import { rowConfig } from '../../data';
import { acceptAIFieldsError } from '../../utilities/acceptAIFieldsError';
import { aiFieldAliasForSecurity, aiRequestFieldForSecurity, dropAiFieldFromColumn, referenceFieldNameSet, referenceFieldsForSecurity } from '../../utilities/aiFieldMap';
import { sameAiValue } from '../../utilities/aiNumericValue';
import { runCapTableClear } from '../../utilities/capTableClear';
import { CAP_TABLE_RESET_API_MODE, type CapTableResetMode, capTableResetSummary } from '../../utilities/capTableReset';
import { applyClearToSecurities, replaceMappingRows, restoredRowsOnCell } from '../../utilities/clearEvidence';
import { isCapTableFieldApplicable } from '../../utilities/fieldApplicability';
import {
  addManualCitation,
  type ManualCitationMode,
  manualCitationRequest,
  type ManualCitationTarget,
  shouldOfferReplace,
} from '../../utilities/manualCitationAdd';
import { mappingProposalIdsFor, mappingRowStatus, pickMappingProposal } from '../../utilities/mappingReferenceStatus';
import {
  type CellCitationPlan,
  cellHasPendingSuggestion,
  citationCellId,
  type CitationTargetCell,
  citationTargetRejection,
  classifyCitationTargets,
  clearCellTargets,
  type ManualCitationPayload,
  pendingReviewItemsForSelection,
  planApplyCitationToCells,
  planClearCells,
  reviewCellIdsForSelection,
} from '../../utilities/multiCellCitationActions';
import {
  isSavedSecurityId,
  stripEditableMappings,
  writeCreatedCitation,
  writeCreatedMapping,
} from '../../utilities/referenceWrites';
import { resolveAcceptValue } from '../../utilities/resolveAiOptionValue';
import { aggregateReviewItems, pickActiveCitation, reviewItemId } from '../../utilities/reviewAggregator';
import {
  buildReviewBatch,
  type BulkFailure,
  bulkFailureSummary,
  evidenceActionOf,
  mappingChangeOf,
  pairBatchResults,
  partitionAcceptableItems,
  type ReviewBulkAction,
  settledFieldsBySecurity,
} from '../../utilities/reviewBulk';
import { undoReviewComposition } from '../../utilities/reviewComposition';
import {
  acceptRefsForFields,
  applyReopenToSecurity,
  applySettledFieldsToSecurities,
  cellHasReferenceGreen,
  decisionFieldFor,
  declineRefsForFields,
  isAiCreatedCitation,
  isPendingMeta,
  reopenRefsForFields,
  resolveDeclineRevert,
  SETTLE_SECURITY,
  syncReferenceGreen,
} from '../../utilities/securityAiPatches';
import {
  applyCitationReviewToCell,
  applyCitationReviewToSecurities,
  settleCitationReview,
} from '../../utilities/settleCitationReview';
import {
  applyMappingReviewToCell,
  applyMappingReviewToSecurities,
  holdCardOnPendingProposal,
  type MappingReviewAction,
  settleMappingReview,
} from '../../utilities/settleMappingReview';
import useGridCitationSelectionStore from '../../utilities/useGridCitationSelectionStore';
import useSelectionGuard from '../../utilities/useSelectionGuard';
import { useAIFieldAcceptContext } from '../captable-table/AIFieldAcceptContext';
import { CapTableCellWithReferences } from '../captable-table/CaptableTable.props';
import { useCitationContext } from '../captable-table/CitationContext';
import { CreateCapTableCitationsDialog, useCreateCapTableCitationsAction } from '../create-citations';
import GridCitationSelectionToolbar from '../grid-citation-selection/GridCitationSelectionToolbar';
import { PendingReviewBadge, ReviewSidePanel } from '../review-side-panel';
import {
  buildCellExcelJump,
  buildReviewDocJump,
  buildReviewExcelJump,
  clearCellSourceReveal,
  revealCellCitationSource,
} from '../review-side-panel/reviewDocJump';
import type { ReviewBulkResult } from '../review-side-panel/ReviewSidePanel.props';

// Ctrl+Shift-click (Cmd+Shift-click on Mac) selects a cell for the citation actions.
const isCitationSelectClick = (event: MouseEvent) =>
  event.button === 0 && (event.ctrlKey || event.metaKey) && event.shiftKey;

// An Excel pick waiting to be saved on its cell's security, with the value it wrote into the cell.
type ExcelPick = { cell: CapTableCellWithReferences; pick: MappingReferenceData; value: unknown };

// The cap table loader: id, write to the store, force, and a check that the selection is still current.
type LoadCapTable = (id?: number, update?: boolean, force?: boolean, shouldApply?: () => boolean) => Promise<boolean>;

// The latest-state ref's starting names; the first render replaces them before anything reads them.
const NO_DOCUMENT_NAMES = new Map<number, string>();

// An unsaved column's pick stays on the cell for Save, replacing its editable references and the pending AI
// proposals from the same workbook; true when it took any row off.
const placeUnsavedPick = (cell: CapTableCellWithReferences, picked: MappingReferenceData[]) => {
  const stripped = stripEditableMappings(cell);
  const pickedFiles = new Set(picked.map(pick => pick.document_reference));
  const [dropped, kept] = partition(
    cell.mappingReferences ?? [],
    ref => mappingRowStatus(ref) === 'pending' && pickedFiles.has(ref.document_reference)
  );
  cell.mappingReferences = [...picked, ...kept];
  syncReferenceGreen(cell);
  return stripped || dropped.length > 0;
};

// A field decision as its grid cell shows it: the AI status, and the field's AI citations unless an accept leaves them.
const FIELD_DECISION_ON_CELL = {
  accept: { status: 'accepted', is_accepted: true, settleRefs: acceptRefsForFields },
  decline: { status: 'dismissed', is_accepted: false, settleRefs: declineRefsForFields },
  reopen: { status: 'pending', is_accepted: null, settleRefs: reopenRefsForFields },
} as const;

const stampFieldDecision = (
  cell: CapTableCellWithReferences | undefined,
  fieldAlias: string,
  action: ReviewBulkAction,
  options?: { settleCitations?: boolean },
  fieldNames = [fieldAlias]
) => {
  if (!cell?.aiFieldMetadata) {
    return;
  }
  const { settleRefs, ...stamp } = FIELD_DECISION_ON_CELL[action];
  cell.aiFieldMetadata = { ...cell.aiFieldMetadata, ...stamp };
  if (options?.settleCitations !== false) {
    cell.citationReferences = settleRefs(cell.citationReferences, fieldNames);
  }
};

// One error toast per distinct reason, since a document-level refusal fails every cell of a batch alike.
const toastDistinctFailures = (results: PromiseSettledResult<unknown>[], fallback: (index: number) => string) => {
  const messages = new Set(
    results.flatMap((result, index) =>
      result.status === 'rejected' ? [extractResponseErrorMessage(result.reason) ?? fallback(index)] : []
    )
  );
  messages.forEach(message => enqueueSnackbar(message, { variant: 'error' }));
};

// "Value kept" lives only on the store cards, never in the cells, so a rebuild carries it over by card id while the
// card is still pending.
const withValueKeptFlags = (items: ReviewItem[]): ReviewItem[] => {
  if (items.length === 0) {
    return items;
  }
  const keptIds = new Set(useReviewStore.getState().reviewItems.flatMap(item => (item.valueKept ? [item.id] : [])));
  return items.map(item =>
    item.status === 'pending' && item.valueKept == null && keptIds.has(item.id) ? { ...item, valueKept: true } : item
  );
};

export interface CapTableWidgetDrawerProps extends WidgetDrawerProps {
  workbook: IWorkbook;
  onTableChange: (cell: any, value: number) => void;
  captableSetProgrammaticUpdate?: (isProgrammatic: boolean) => void;
  /** The version selector's own id; unlike the store's cap table it survives a reload. */
  selectedVersionId?: string | number | null;
  /** Grid whose cells Ctrl+Shift+click selects. */
  gridContainerRef?: RefObject<HTMLElement | null>;
  /** Navigation element the citation selection toolbar renders into. */
  gridSelectionToolbarHost?: HTMLElement | null;
}

const CapTableWidgetDrawer = ({
  onTableChange,
  workbook,
  spreadsheetNotesProps,
  notesProps,
  documentReferencesProps,
  applicationContext,
  documentApplicationStorageKey,
  captableSetProgrammaticUpdate,
  selectedVersionId,
  gridContainerRef,
  gridSelectionToolbarHost,
}: CapTableWidgetDrawerProps) => {
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { captableInfo, companyInfo } = storeValue;
  // A rebuild can land while a request awaits the server, so code after an await reads the current store, cells,
  // grid change handler and document names here; it is refreshed each render, right after the document-name memo.
  const latestReviewState = useRef({
    captableInfo,
    workbook,
    onTableChange,
    documentNamesByRefId: NO_DOCUMENT_NAMES,
    unsavedChanges: false as UnsavedChangesType['action'],
  });
  // One cell per (security column, field alias), read from the current cells so a rebuild's replacement is found.
  const findReviewCell = useCallback(
    (securityId: number, fieldAlias: string) => {
      const security = latestReviewState.current.captableInfo?.securities?.find(item => item.id === securityId);
      const alias = fieldAlias === 'shares_outstanding' ? aiFieldAliasForSecurity(security, fieldAlias) : fieldAlias;
      return Object.values(
        (latestReviewState.current.workbook?.captable?.cells ?? {}) as Record<string, CapTableCellWithReferences>
      ).find(cell => Number(cell.columnId) === securityId && cell.alias === alias);
    },
    []
  );
  // The cached store security a review decision or reference write reads, from the current store for the same reason.
  const storeSecurity = useCallback(
    (securityId: number) =>
      (latestReviewState.current.captableInfo?.securities as SecurityModel[] | undefined)?.find(
        security => security.id === securityId
      ),
    []
  );
  // Saves for one cell run one after another, so each request starts from what the previous one wrote.
  const cellSaves = useRef(new Map<string, Promise<void>>());
  const queueCellSave = (securityId: number, alias: string, save: () => Promise<void>) => {
    const key = reviewItemId(securityId, alias);
    const queued = (cellSaves.current.get(key) ?? Promise.resolve()).catch(() => undefined).then(save);
    cellSaves.current.set(key, queued);
    return queued;
  };
  const { currentMeasurementDate } = useLayoutContext();
  const { getCellMapping } = useCellMappingStore();
  const { attributeMapperManager, getAttributeMapperManager, clearAttributeMapperManager } = useAttributeMapperStore();
  const { registerFootnote, clearFootnotes } = useDocumentFootnotes();
  const { addJob, removeJob } = useAsyncProcessor();
  const { acceptField, dismissField, dismissAiColumn } = useAIFieldAcceptContext();
  const setReviewItems = useReviewStore(s => s.setReviewItems);
  const resetReviewSession = useReviewStore(s => s.resetReviewSession);
  // Pending-only count: reviewed items still live in the store (for the muted
  // Reviewed section), so the full length would keep the drawer auto-opening.
  const pendingReviewCount = usePendingReviewCount();
  // Withhold the side panel when the visibility selector marks it collapsed.
  const reviewPaneCollapsed = useReviewPaneCollapsed();
  const setPaneCollapsed = useReviewStore(s => s.setPaneCollapsed);
  const resetReviewFilter = useReviewStore(s => s.resetReviewFilter);
  // SCAL-9335: the AI review experience is analyst-side only. Non-reviewers
  // (clients / view-only roles) get the plain cap table: no review panel or
  // badge, no auto-open, no review items - and the stamped store flag below
  // keeps the grid viewers plain (normal blue citations, no AI hue).
  const isAiReviewer = useAiReviewerRole();
  const setReviewerMode = useReviewStore(s => s.setReviewerMode);
  useEffect(() => {
    setReviewerMode(isAiReviewer);
  }, [isAiReviewer, setReviewerMode]);
  const patchSecurities = usePatchCapTableSecurities();
  const [, getCapTableInfo] = useGetCaptableInfo();
  // The page's unsaved-changes flag, which a landed reload clears with the edits it discards.
  const { action: unsavedChanges, cleanAction: clearUnsavedChanges } = useContext(UnsavedChangesContext);
  const capTableService = useMemo(() => new CapatableService(), []);
  const drawerSelection = documentReferencesProps as
    | { referencedFeatureId?: number | string; selectedMeasurementDate?: { id?: number }; currentPage?: string }
    | undefined;
  // The version selector's id first: the store's cap table is blanked while a load runs.
  const selectedCapTableId = Number(selectedVersionId ?? drawerSelection?.referencedFeatureId) || null;
  const selectedMeasurementDateId = drawerSelection?.selectedMeasurementDate?.id ?? currentMeasurementDate?.id ?? null;
  // Identity for late replies: company, date and version, none of which a load blanks.
  const guardSelection = useSelectionGuard(
    `${companyInfo?.id ?? null}:${selectedMeasurementDateId}:${selectedVersionId ?? null}`
  );
  // Reloads a cap table, dropping a reply that lands after the analyst moved on. A landed reload replaced the
  // unsaved edits, so it clears their flag; a failed one keeps it, and with it the leave-page guard.
  const refreshCapTable = useCallback(
    async (targetCapTableId: number, stillCurrent: () => boolean) => {
      if (await (getCapTableInfo as LoadCapTable)(targetCapTableId, true, true, stillCurrent)) {
        clearUnsavedChanges();
      }
    },
    [getCapTableInfo, clearUnsavedChanges]
  );
  const { setActiveCitation } = useCitationContext();

  const { publish } = useEventBus();
  const [citation, setCitation] = useState<(CitationPushedPayload & { documentReferenceId: number }) | undefined>();
  const gridSelectionMode = useGridCitationSelectionStore(s => s.mode);
  const gridSelected = useGridCitationSelectionStore(s => s.selected);
  // While cells are being selected, plain clicks neither jump to a source nor place a citation.
  const selectingCells = () => useGridCitationSelectionStore.getState().mode === 'add-multiple';
  const setFilterCellIds = useReviewStore(s => s.setFilterCellIds);
  // SCAL-8970: the cell awaiting an explicit Append-vs-Replace choice; non-null
  // keeps the confirm dialog open while the pushed citation stays pending.
  const [replacePromptCell, setReplacePromptCell] = useState<CapTableCellWithReferences | null>(null);
  const [gridCitationBusy, setGridCitationBusy] = useState(false);
  const gridCitationBusyRef = useRef(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [createCitationsEnabled, setCreateCitationsEnabled] = useState(false);
  const createCitationsAction = useCreateCapTableCitationsAction({ enabled: createCitationsEnabled });
  const [reprocessDialogOpen, setReprocessDialogOpen] = useState(false);
  const [reprocessJobMetadata, setReprocessJobMetadata] = useState<{
    document_reference_id: number;
    measurement_date_id?: number | null;
    file_id?: number;
  } | null>(null);
  // Registers a footnote per cited cell, read from the current cells so a call after an await sees a rebuild's cells.
  const registerCapTableCitationFootnotes = () => {
    const cells = latestReviewState.current.workbook?.captable?.cells as
      | Record<string, CapTableCellWithReferences>
      | undefined;
    if (!cells) {
      return;
    }

    const cellsWithCitations = Object.values(cells).filter(
      cell => cell.citationReferences && cell.citationReferences.length > 0
    );

    if (cellsWithCitations.length === 0) {
      clearFootnotes();
      return;
    }

    // Group citations by cell to create unique footnote IDs
    cellsWithCitations.forEach(cell => {
      const cellKey = cell.key || `${cell.columnLegend}${cell.rowNumber}`;

      if (!cell.citationReferences) {
        return;
      }

      // Convert CitationReferenceData to DocumentReference format
      const documentReferencesMap = new Map<number, DocumentReference>();

      cell.citationReferences.forEach(citationRef => {
        const docId = citationRef.document_reference;

        if (!documentReferencesMap.has(docId)) {
          documentReferencesMap.set(docId, {
            documentId: docId,
            fields: [],
          });
        }

        const docRef = documentReferencesMap.get(docId)!;

        // For citations, we use the field_name and create pseudo cell references from token positions
        const existingFieldIndex = docRef.fields.findIndex(f => f.fieldName === citationRef.field_name);

        const cellReferences = citationRef.tokenReferences.map(tokenRef => ({
          ref: `p${tokenRef.page}-${tokenRef.token_ids.join(',')}`,
          direction: 'positive' as const,
        }));

        if (existingFieldIndex >= 0) {
          // Merge cell references into existing field
          docRef.fields[existingFieldIndex].cellReferences.push(...cellReferences);
        } else {
          // Add new field entry
          docRef.fields.push({
            fieldName: citationRef.field_name,
            sheetName: 'Citation', // Citations don't have sheet names, use a placeholder
            cellReferences,
          });
        }
      });

      // Register footnote for this cell
      registerFootnote(`captable-cell-${cellKey}`, Array.from(documentReferencesMap.values()));
    });
  };

  // Activating a grid cell reveals its source. A CITED cell (SCAL-9182) is the
  // reverse of the card-to-cell link: select its review card - opening the pane and
  // drawer if hidden - and jump the doc view to the citation, exactly like the
  // card's eyeball. Uncited cells keep the pre-existing Excel-mapping navigation.
  // While a pushed citation is pending, the attach subscription below owns the click.
  useEventSubscription(
    EventType.SPREADSHEET_ACTIVE_CELL_CHANGED,
    ([newCell]: [{ cellInfo: CapTableCellWithReferences }]) => {
      if (citation || selectingCells()) {
        return;
      }
      const cell = newCell.cellInfo;
      const navigate = (payload: NavigateToDocumentReferencePayload) =>
        publish(EventType.NAVIGATE_TO_DOCUMENT_REFERENCE, payload);
      if (revealCellCitationSource(cell, setActiveCitation, navigate)) {
        return;
      }
      clearCellSourceReveal(setActiveCitation);
      const excelJump = buildCellExcelJump(cell);
      if (excelJump) {
        navigate(excelJump.navigatePayload);
      }
    }
  );

  const capTableCells = useCallback(
    (): CapTableCellWithReferences[] =>
      Object.values((workbook?.captable?.cells ?? {}) as Record<string, CapTableCellWithReferences>),
    [workbook?.captable?.cells]
  );

  // The selection gesture is taken at window capture and swallowed there, so the grid never
  // sees it and its own highlighted range cannot move or spread.
  useEffect(() => {
    if (!isAiReviewer || !gridContainerRef) {
      return undefined;
    }
    let swallowing = false;
    const swallow = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
    };
    const gridCellAt = (event: MouseEvent) => {
      const td = event.target instanceof Element ? event.target.closest<HTMLElement>('td') : null;
      return td && gridContainerRef.current?.contains(td) ? td : null;
    };
    const onMouseDown = (event: MouseEvent) => {
      const td = isCitationSelectClick(event) ? gridCellAt(event) : null;
      swallowing = td !== null;
      if (!td) {
        return;
      }
      swallow(event);
      const { columnId, alias } = td.dataset;
      const cell: CitationTargetCell = capTableCells().find(
        entry => String(entry.columnId) === columnId && entry.alias === alias
      ) ?? { isLegend: td.classList.contains('legend'), columnId, alias };
      const reason = citationTargetRejection(cell);
      const id = citationCellId(cell);
      if (reason || !id || cell.alias == null || cell.columnId == null) {
        enqueueSnackbar(reason ?? 'This cell is not a security field', { variant: 'error' });
        return;
      }
      const selection = useGridCitationSelectionStore.getState();
      if (selection.mode !== 'add-multiple') {
        selection.enterAddMultiple();
      }
      selection.toggle({ id, alias: cell.alias, columnId: cell.columnId, key: cell.key });
      // Deselecting the last cell also lifts the review filter it put on that cell, leaving any
      // other filtered cells in place.
      const { filterCellIds } = useReviewStore.getState();
      if (useGridCitationSelectionStore.getState().selected.length === 0 && filterCellIds.has(id)) {
        setFilterCellIds([...filterCellIds].filter(filterId => filterId !== id));
      }
    };
    const onFollowUp = (event: MouseEvent) => {
      if (swallowing) {
        swallow(event);
        swallowing = event.type !== 'click';
      }
    };
    window.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('mouseup', onFollowUp, true);
    window.addEventListener('click', onFollowUp, true);
    return () => {
      window.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('mouseup', onFollowUp, true);
      window.removeEventListener('click', onFollowUp, true);
    };
  }, [isAiReviewer, gridContainerRef, capTableCells, setFilterCellIds]);

  const selectedCapTableCells = () => {
    const byId = new Map(capTableCells().map(cell => [citationCellId(cell), cell]));
    return gridSelected
      .map(item => byId.get(item.id))
      .filter((cell): cell is CapTableCellWithReferences => Boolean(cell));
  };

  // Every document the cells' citations and Excel references point at, for the review cards' names.
  const referencedDocumentIds = useMemo(() => {
    const cells = workbook?.captable?.cells as Record<string, CapTableCellWithReferences> | undefined;
    if (!cells) {
      return [] as string[];
    }
    const ids = new Set<string>();
    Object.values(cells).forEach(cell => {
      [...(cell.citationReferences ?? []), ...(cell.mappingReferences ?? [])].forEach(ref => {
        if (ref.document_reference != null) {
          ids.add(String(ref.document_reference));
        }
      });
    });
    return Array.from(ids);
  }, [workbook?.captable?.cells]);

  // The names load once the browser is idle, so they never hold up the document the analyst opens.
  const [docNamesEnabled, setDocNamesEnabled] = useState(false);
  useEffect(() => {
    const idleWindow = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    if (idleWindow.requestIdleCallback) {
      const handle = idleWindow.requestIdleCallback(() => setDocNamesEnabled(true), { timeout: 2500 });
      return () => idleWindow.cancelIdleCallback?.(handle);
    }
    const handle = window.setTimeout(() => setDocNamesEnabled(true), 1200);
    return () => window.clearTimeout(handle);
  }, []);

  const { data: referencedDocuments } = useGetDocumentsById({
    documentIds: referencedDocumentIds,
    enabled: docNamesEnabled,
  });

  const documentNamesByRefId = useMemo(() => {
    const map = new Map<number, string>();
    Object.values(referencedDocuments ?? {}).forEach(doc => {
      if (doc.file?.filename) {
        map.set(doc.document_reference_id, doc.file.filename);
      }
    });
    return map;
  }, [referencedDocuments]);
  latestReviewState.current = { captableInfo, workbook, onTableChange, documentNamesByRefId, unsavedChanges };

  // Rebuilds the review cards from the latest grid cells and document names; only AI reviewers get cards.
  const refreshReviewItems = useCallback(() => {
    const { workbook: latest, documentNamesByRefId: names } = latestReviewState.current;
    const cells = latest?.captable?.cells as Record<string, CapTableCellWithReferences> | undefined;
    const items =
      isAiReviewer && cells ? withValueKeptFlags(aggregateReviewItems(cells, names, latest?.captable?.tableData)) : [];
    setReviewItems(items);
    return items;
  }, [isAiReviewer, setReviewItems]);

  // Rebuilds the review cards and the citation footnotes after a reference write changed the cells.
  const refreshCellEvidence = () => {
    refreshReviewItems();
    registerCapTableCitationFootnotes();
  };

  // Repaints one cell through the grid's conditions, optionally writing a new value so its dependents recalc. It runs
  // as programmatic because a manual edit would clear the cell's AI suggestion, Excel reference and pending AI citations.
  const repaintCell = useCallback(
    (cell: CapTableCellWithReferences, value: unknown = cell.value) => {
      captableSetProgrammaticUpdate?.(true);
      try {
        latestReviewState.current.onTableChange(cell, value as number);
      } finally {
        captableSetProgrammaticUpdate?.(false);
      }
    },
    [captableSetProgrammaticUpdate]
  );

  // Repaints a cell an Excel review took the green off: a cell event can add the green, only a repaint removes it.
  // Any other review needs no repaint, which would rerun the sheet's conditions and its server recalculation.
  const repaintLostGreen = useCallback(
    (cell: CapTableCellWithReferences | undefined, wasGreen: boolean) => {
      if (cell && wasGreen && !cellHasReferenceGreen(cell)) {
        repaintCell(cell);
      }
    },
    [repaintCell]
  );

  // Saves a manual citation on a saved security after any earlier save on its cell, building the request from
  // the cell as that save left it, and writes the server's row onto the cell and the store.
  const saveManualCitation = (
    cell: CapTableCellWithReferences,
    target: ManualCitationTarget,
    mode: ManualCitationMode
  ) => {
    const securityId = Number(cell.columnId);
    const alias = String(cell.alias);
    const stillCurrent = guardSelection();
    return queueCellSave(securityId, alias, async () => {
      const live = findReviewCell(securityId, alias) ?? cell;
      const result = await capTableService.createSecurityCitation(
        securityId,
        manualCitationRequest(live.citationReferences, target, mode)
      );
      // A reply that lands after the analyst moved to another table must not touch the new one.
      if (!stillCurrent()) {
        return;
      }
      const saved = findReviewCell(securityId, alias);
      writeCreatedCitation(saved, storeSecurity(securityId), result);
      if (saved) {
        repaintCell(saved);
      }
    });
  };

  // Finishes a pending manual citation on `cell`: a saved security saves it at once, an unsaved column
  // keeps it on the cell until Save creates the security.
  const completeManualCitationAdd = async (cell: CapTableCellWithReferences, mode: ManualCitationMode) => {
    if (!citation) {
      return;
    }
    const staged = citation;
    const target: ManualCitationTarget = {
      fieldName: cell.alias,
      documentReferenceId: staged.documentReferenceId,
      tokenReference: { token_ids: staged.tokenIds, page: staged.page, plain_text: staged.selectedText, notes: [] },
    };
    setCitation(undefined);
    setReplacePromptCell(null);
    if (isSavedSecurityId(cell.columnId)) {
      const stillCurrent = guardSelection();
      try {
        await saveManualCitation(cell, target, mode);
      } catch (error) {
        // The selection stays staged, so another click can retry; a stale reply reports nothing.
        if (stillCurrent()) {
          setCitation(current => current ?? staged);
          enqueueSnackbar(
            extractResponseErrorMessage(error) ?? `Failed to add the citation to ${cell.alias}(${cell.key})`,
            { variant: 'error' }
          );
        }
        return;
      }
      // A save that lands after the analyst moved to another table reports nothing.
      if (!stillCurrent()) {
        return;
      }
    } else {
      cell.citationReferences = addManualCitation(cell.citationReferences, target, mode);
      repaintCell(cell);
    }
    enqueueSnackbar(`Citation added to ${cell.alias}(${cell.key})`, { variant: 'success' });
    refreshCellEvidence();
  };

  // The cards follow the cells, and refill once the document names arrive.
  useEffect(() => {
    refreshReviewItems();
  }, [workbook?.captable?.cells, documentNamesByRefId, refreshReviewItems]);

  const persistCitationPlans = (plans: CellCitationPlan[]) => {
    const mutated = plans.filter(plan => plan.mutated);
    if (mutated.length === 0) {
      return 0;
    }
    mutated.forEach(({ cell, citationReferences, mappingReferences, clearsSuggestion }) => {
      const live = cell as CapTableCellWithReferences;
      live.citationReferences = citationReferences;
      if (clearsSuggestion) {
        // Clear the column's evidence too, so rebuilding cells keeps the selected field clear.
        const column = latestReviewState.current.workbook?.captable?.tableData?.columns?.find(
          (candidate: { id?: unknown }) => candidate.id === live.columnId
        );
        const fieldNames = referenceFieldNameSet(
          referenceFieldsForSecurity(storeSecurity(Number(live.columnId)), String(live.alias))
        );
        dropAiFieldFromColumn(column, fieldNames);
        if (column) {
          (['citation_references', 'mapping_references'] as const).forEach(key => {
            const refs = column[key];
            if (Array.isArray(refs)) column[key] = refs.filter(ref => !fieldNames.has(ref.field_name));
          });
        }
        undoReviewComposition(live);
      }
      if (mappingReferences !== undefined) {
        live.mappingReferences = mappingReferences;
        syncReferenceGreen(live);
        if (mappingReferences.length === 0 && citationReferences.length === 0) {
          live.columnarReferences = [];
          live.columnarReferencesMetadata = [];
        }
      }
      repaintCell(live);
    });
    refreshCellEvidence();
    return mutated.length;
  };

  // Runs one toolbar action at a time, then leaves selection mode.
  const runGridSelectionAction = async (action: () => Promise<void>) => {
    if (!isAiReviewer || gridCitationBusyRef.current) {
      return;
    }
    gridCitationBusyRef.current = true;
    setGridCitationBusy(true);
    try {
      await action();
    } finally {
      gridCitationBusyRef.current = false;
      setGridCitationBusy(false);
      useGridCitationSelectionStore.getState().exit();
    }
  };

  /** Mirror a server clear onto the cached securities in place; a replaced store would rebuild the grid. */
  const writeClearToStore = useCallback((result: Partial<ClearEvidenceResult>) => {
    const securities = latestReviewState.current.captableInfo?.securities as SecurityModel[] | undefined;
    if (!securities) {
      return;
    }
    applyClearToSecurities(securities, result).forEach((row, index) => {
      securities[index] = row as SecurityModel;
    });
  }, []);

  // Clear keeps only the values: the server retires every citation, Excel reference and AI suggestion on the cells.
  const handleGridSelectionClear = () =>
    runGridSelectionAction(async () => {
      const plans = planClearCells(selectedCapTableCells());
      if (plans.every(plan => !plan.mutated)) {
        enqueueSnackbar('No citations to clear', { variant: 'info' });
        return;
      }
      // A selection of only unsaved columns has nothing stored, so it skips the server.
      const targets = clearCellTargets(plans, latestReviewState.current.captableInfo?.securities);
      const stillCurrent = guardSelection();
      let result: Partial<ClearEvidenceResult> = {};
      if (targets.length > 0) {
        const capTableId = captableInfo?.id;
        if (!capTableId) {
          enqueueSnackbar('Cap table is not loaded', { variant: 'error' });
          return;
        }
        try {
          result = await capTableService.clearCells(capTableId, targets);
        } catch (error) {
          Logger.error('CapTableWidgetDrawer - clear cells failed', error);
          enqueueSnackbar(extractResponseErrorMessage(error) ?? 'Failed to clear the selected cells', {
            variant: 'error',
          });
          return;
        }
      }
      // A reply that lands after the analyst moved to another table must not touch the new one.
      if (!stillCurrent()) {
        return;
      }
      const count = persistCitationPlans(plans);
      writeClearToStore(result);
      enqueueSnackbar(`Cleared citations and references on ${count} cell${count === 1 ? '' : 's'}`, {
        variant: 'success',
      });
    });

  const handleGridSelectionAttach = () => {
    if (!citation) {
      enqueueSnackbar('Select a citation in the document first', { variant: 'error' });
      return;
    }
    const staged = citation;
    const payload: ManualCitationPayload = {
      documentReferenceId: staged.documentReferenceId,
      tokenReference: {
        token_ids: staged.tokenIds,
        page: staged.page,
        plain_text: staged.selectedText,
        notes: [],
      },
    };
    runGridSelectionAction(async () => {
      const { supported, rejected } = classifyCitationTargets(selectedCapTableCells());
      rejected.forEach(item => enqueueSnackbar(item.reason, { variant: 'error' }));
      // Saved securities save the citation at once, one request per cell; unsaved columns keep it until Save.
      const [saved, unsaved] = partition(supported as CapTableCellWithReferences[], cell =>
        isSavedSecurityId(cell.columnId)
      );
      const stillCurrent = guardSelection();
      const results = await Promise.allSettled(
        saved.map(cell => saveManualCitation(cell, { ...payload, fieldName: cell.alias }, 'append'))
      );
      // A reply that lands after the analyst moved to another table must not touch the new one.
      if (!stillCurrent()) {
        return;
      }
      toastDistinctFailures(results, index => `Failed to add the citation to ${saved[index].alias}(${saved[index].key})`);
      const localCount = persistCitationPlans(planApplyCitationToCells(unsaved, payload).applied);
      // persistCitationPlans ends with the refresh when it changed a cell; otherwise refresh once for the saves.
      if (localCount === 0) {
        refreshCellEvidence();
      }
      const count = results.filter(result => result.status === 'fulfilled').length + localCount;
      if (count > 0) {
        enqueueSnackbar(`Citation added to ${count} cell${count === 1 ? '' : 's'}`, { variant: 'success' });
        // A passage staged while the saves ran stays for the next cell.
        setCitation(current => (current === staged ? undefined : current));
      }
    });
  };

  const handleGridSelectionFilter = () => {
    const ids = reviewCellIdsForSelection(selectedCapTableCells());
    setFilterCellIds(ids);
    setPaneCollapsed(false);
    enqueueSnackbar(
      ids.length > 0
        ? `Review filtered to ${ids.length} cell${ids.length === 1 ? '' : 's'}`
        : 'No security fields selected',
      { variant: ids.length > 0 ? 'success' : 'info' }
    );
    useGridCitationSelectionStore.getState().exit();
  };

  const handleGridSelectionCancel = () => {
    useGridCitationSelectionStore.getState().exit();
    setFilterCellIds([]);
  };

  // this might move down into the md viewer
  useEventSubscription(
    EventType.SPREADSHEET_ACTIVE_CELL_CHANGED,
    ([newCell, _prevCell]: [{ cellInfo: CapTableCellWithReferences }, CapTableCellWithReferences]) => {
      const cell = newCell.cellInfo;
      if (!citation || selectingCells()) {
        return;
      }

      // SCAL-8970: adding on top of a visible AI citation is a deliberate
      // choice - prompt for Append (the default) vs Replace, never decide
      // silently. Every other add appends exactly as before.
      if (shouldOfferReplace(cell.citationReferences, cell.alias, citation.documentReferenceId)) {
        setReplacePromptCell(cell);
        return;
      }

      completeManualCitationAdd(cell, 'append');
    }
  );

  // Update footnotes whenever citations in cells change
  useEffect(() => {
    registerCapTableCitationFootnotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workbook?.captable?.cells]);

  // Cleanup footnotes on unmount
  useEffect(
    () => () => {
      clearFootnotes();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Each version starts with fresh selection, filters and default panel visibility.
  useEffect(() => {
    useGridCitationSelectionStore.getState().exit();
    resetReviewFilter();
  }, [selectedVersionId, resetReviewFilter]);

  // Leaving the sheet drops everything scoped to it - the grid selection and the
  // whole review session (items, filter, pane flag, reviewer stamp) - so a
  // different page's drawer never inherits them.
  useEffect(
    () => () => {
      useGridCitationSelectionStore.getState().exit();
      resetReviewSession();
    },
    [resetReviewSession]
  );

  // SCAL-8976 (fix #1): when the cap table loads with pending review items, open
  // the bottom drawer ONCE so the Pending Review panel is visible - for analysts
  // only (SCAL-9335). Guarded by a ref so it never re-opens after the user closes
  // it (expandDrawer is itself a no-op when the drawer is already open, so a
  // user-resized drawer is untouched).
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (!isAiReviewer || autoOpenedRef.current || pendingReviewCount === 0) {
      return;
    }
    autoOpenedRef.current = true;
    publish(EventType.EXPAND_DRAWER, undefined);
  }, [isAiReviewer, pendingReviewCount, publish]);

  // A field decision lands on its cell in place, because replacing the store re-parses the whole grid for seconds.
  // The store security is written in place too, so a later rebuild from the store never brings it back as pending.
  const settleFieldOnStore = useCallback(
    (
      securityId: number,
      fieldAlias: string,
      action: 'accept' | 'decline' | 'reopen',
      options?: { settleCitations?: boolean }
    ) => {
      const securities = latestReviewState.current.captableInfo?.securities as SecurityModel[] | undefined;
      const idx = securities?.findIndex(s => s.id === securityId) ?? -1;
      if (securities && idx >= 0) {
        // Underlying-security names resolve against the sibling securities.
        securities[idx] = SETTLE_SECURITY[action](securities[idx], [fieldAlias], securities, options) as SecurityModel;
      }
    },
    []
  );

  // A settled security name shows on its header cell at once. An accept writes the new name to the cell's value and
  // expression, which the editor and copy read; the securities patch that settles the name then rebuilds the column.
  const settleNameOnGrid = useCallback(
    (securityId: number, action: ReviewBulkAction, options?: { settleCitations?: boolean }) => {
      const cell = findReviewCell(securityId, SECURITY_NAME_ALIAS);
      const acceptedName =
        action === 'accept' && isPendingMeta(cell?.aiFieldMetadata) ? cell?.aiFieldMetadata?.value : undefined;
      stampFieldDecision(cell, SECURITY_NAME_ALIAS, action, options);
      if (cell && acceptedName != null) {
        cell.value = String(acceptedName);
        cell.expr = String(acceptedName);
        cell.dispatchEvent?.();
      }
    },
    [findReviewCell]
  );

  // Recheck the current grid before accepting a value or its evidence from a stale card.
  const acceptErrorFor = useCallback(
    (securityId: number, fieldAlias: string, mode: 'value' | 'evidence' = 'value'): string | undefined => {
      const cell = findReviewCell(securityId, fieldAlias);
      const sheet = latestReviewState.current.workbook?.captable;
      if (!cell || !isCapTableFieldApplicable(cell, sheet?.cells ?? {}, mode, sheet?.tableData)) {
        return 'This field does not apply to the current security and settings';
      }
      return mode === 'value' && cell.aiFieldMetadata ? resolveAcceptValue(cell).error : undefined;
    },
    [findReviewCell]
  );

  const acceptFieldOnGrid = useCallback(
    (securityId: number, fieldAlias: string, options?: { settleCitations?: boolean }) => {
      const cell = findReviewCell(securityId, fieldAlias);
      if (!cell?.aiFieldMetadata) {
        return;
      }
      // Select-backed cells carry the option NAME in the AI metadata while the grid
      // stores the option ID: resolve BEFORE writing (shared resolver). Unresolvable
      // suggestions stay pending - callers pre-check via acceptErrorFor before the
      // server settle; this guard covers any path that did not.
      const { value: aiValue, error } = resolveAcceptValue(cell);
      if (error) {
        enqueueSnackbar(error, { variant: 'error' });
        return;
      }
      // A decision writing another field than the card's cell (its type changed) rebuilds the grid from the store.
      const moved = decisionFieldFor(storeSecurity(securityId), String(cell.alias), 'accept') !== cell.alias;
      settleFieldOnStore(securityId, fieldAlias, 'accept', options);
      // The accept also settles the field's AI citations, unless it leaves them for a later citation dismiss.
      stampFieldDecision(cell, fieldAlias, 'accept', options, referenceFieldsForSecurity(storeSecurity(securityId), fieldAlias));
      if (!moved && aiValue != null && !sameAiValue(aiValue, cell.value)) {
        // The accepted value goes through the grid so dependents (ownership %, fully-diluted) recalc.
        repaintCell(cell, aiValue);
      } else {
        cell.dispatchEvent?.();
      }
      if (moved || cell.alias === 'options_ledger') {
        patchSecurities(securities => [...securities]);
      }
    },
    [findReviewCell, settleFieldOnStore, repaintCell, storeSecurity, patchSecurities]
  );

  const dismissFieldOnGrid = useCallback(
    (securityId: number, fieldAlias: string) => {
      const cell = findReviewCell(securityId, fieldAlias);
      if (!cell?.aiFieldMetadata) {
        return;
      }
      // SCAL-9337: decide the value revert from the PENDING entry before the stamp.
      // A cell holding the AI value (purely AI-generated rows, accepted-then-
      // declined) reverts to the previous_value baseline - EMPTY when none, so
      // save validation forces an explicit entry; a merge suggestion never applied
      // its value, so the prior value already on the cell survives untouched.
      const moved = decisionFieldFor(storeSecurity(securityId), String(cell.alias), 'decline') !== cell.alias;
      const { revert, value: revertValue } = moved
        ? { revert: false, value: undefined }
        : resolveDeclineRevert(
          cell.aiFieldMetadata,
          cell.value,
          Array.isArray(cell.options) ? cell.options : undefined,
          cell.alias ?? fieldAlias,
          storeSecurity(securityId)
        );
      settleFieldOnStore(securityId, fieldAlias, 'decline');
      // The Excel reference stays, so a cell with an earlier reference keeps its green.
      stampFieldDecision(cell, fieldAlias, 'decline', undefined, referenceFieldsForSecurity(storeSecurity(securityId), fieldAlias));
      if (revert) {
        repaintCell(cell, revertValue ?? '');
      } else {
        cell.dispatchEvent?.();
      }
      // A revert of another field than the card's cell rebuilds the grid from the store.
      if (moved) {
        patchSecurities(securities => [...securities]);
      }
    },
    [findReviewCell, storeSecurity, settleFieldOnStore, repaintCell, patchSecurities]
  );

  // SCAL-9262 reopen (un-accept / un-dismiss) grid mirror: flip the cell's
  // metadata + AI citation refs back to pending and, when un-accepting a cell
  // that still HOLDS the AI value, take the value back to the previous_value
  // baseline (the same resolveDeclineRevert rule as decline - a manual edit
  // made after acceptance survives). Returns whether the kept value should be
  // FLAGGED on the card: the entry was accepted, the cell holds some value, yet
  // it is not the AI value anymore, so the reopen left it untouched.
  const reopenFieldOnGrid = useCallback(
    (securityId: number, fieldAlias: string, wasAccepted: boolean): { valueKept: boolean } => {
      const cell = findReviewCell(securityId, fieldAlias);
      if (!cell?.aiFieldMetadata) {
        return { valueKept: false };
      }
      // Decide the revert from the SETTLED entry before the stamp (dismiss parity); a revert of another
      // field than the card's cell rebuilds the grid from the store instead.
      const moved = wasAccepted && decisionFieldFor(storeSecurity(securityId), String(cell.alias), 'reopen') !== cell.alias;
      const { revert, value: revertValue } = wasAccepted && !moved
        ? resolveDeclineRevert(
          cell.aiFieldMetadata,
          cell.value,
          Array.isArray(cell.options) ? cell.options : undefined,
          cell.alias ?? fieldAlias,
          storeSecurity(securityId)
        )
        : { revert: false, value: undefined };
      const valueKept =
        wasAccepted && !moved && !revert && cell.aiFieldMetadata.value != null && cell.value != null && cell.value !== '';
      settleFieldOnStore(securityId, fieldAlias, 'reopen');
      stampFieldDecision(cell, fieldAlias, 'reopen', undefined, referenceFieldsForSecurity(storeSecurity(securityId), fieldAlias));
      if (revert) {
        repaintCell(cell, revertValue ?? '');
      } else {
        cell.dispatchEvent?.();
      }
      if (moved) {
        patchSecurities(securities => [...securities]);
      }
      return { valueKept };
    },
    [findReviewCell, storeSecurity, settleFieldOnStore, repaintCell, patchSecurities]
  );

  // Settle the cell's Excel proposal when this card decision changes it, reading the proposal from the cell's
  // current rows; the caller rebuilds the cards once its whole decision is in. Resolves the card's next status
  // (undefined when no request was needed) and throws when the request fails.
  const persistCellMappingProposal = useCallback(
    async (
      securityId: number,
      fieldAlias: string,
      action: MappingReviewAction,
      cardStatus: ReviewStatus
    ): Promise<ReviewStatus | undefined> => {
      const applicabilityError = action === 'accept' && acceptErrorFor(securityId, fieldAlias, 'evidence');
      if (applicabilityError) {
        throw new Error(applicabilityError);
      }
      const mappingIds = mappingProposalIdsFor(
        findReviewCell(securityId, fieldAlias)?.mappingReferences,
        action,
        cardStatus
      );
      if (mappingIds.length === 0) {
        return undefined;
      }
      const wasGreen = cellHasReferenceGreen(findReviewCell(securityId, fieldAlias));
      const status = await settleMappingReview({
        itemId: reviewItemId(securityId, fieldAlias),
        mappingIds,
        action,
        persist: capTableService.reviewMappings,
        getCell: () => findReviewCell(securityId, fieldAlias),
        getSecurities: () => latestReviewState.current.captableInfo?.securities,
        getReviewStore: useReviewStore.getState,
      });
      repaintLostGreen(findReviewCell(securityId, fieldAlias), wasGreen);
      return status;
    },
    [capTableService, findReviewCell, repaintLostGreen, acceptErrorFor]
  );

  // The same settle next to a field or citation accept / dismiss: a failure is reported and leaves the
  // proposal as it was, for the card to rule on next. Resolves whether a proposal was settled.
  const settleCellMappingProposal = useCallback(
    async (securityId: number, fieldAlias: string, action: MappingReviewAction) => {
      try {
        return (await persistCellMappingProposal(securityId, fieldAlias, action, 'pending')) !== undefined;
      } catch {
        enqueueSnackbar(`Failed to ${action} the Excel mapping`, { variant: 'error' });
        return false;
      }
    },
    [persistCellMappingProposal]
  );

  // Once a card's value or citation is decided, a proposal still waiting on its cell takes the
  // card over as an Excel card: resolves `pending` then, else undefined.
  const holdCardOnCellProposal = useCallback(
    (securityId: number, fieldAlias: string) =>
      holdCardOnPendingProposal(
        reviewItemId(securityId, fieldAlias),
        findReviewCell(securityId, fieldAlias)?.mappingReferences,
        useReviewStore.getState
      ),
    [findReviewCell]
  );

  // A field accept or decline also settles its cell's Excel proposal and rebuilds the cards from the cell: resolves
  // `pending` when a proposal still waits on the cell and takes the card over, else undefined.
  const settleFieldCardProposal = useCallback(
    async (securityId: number, fieldAlias: string, action: 'accept' | 'dismiss') => {
      if (await settleCellMappingProposal(securityId, fieldAlias, action)) {
        refreshReviewItems();
      }
      return holdCardOnCellProposal(securityId, fieldAlias);
    },
    [settleCellMappingProposal, refreshReviewItems, holdCardOnCellProposal]
  );

  // Settle a card's citation on the cell and, in place, on the store security. A citation
  // card's accept or dismiss also settles its cell's Excel proposal (reopen does that first,
  // in handleReviewReopen); a field card's citation settles alone. Throws without a toast.
  const settleCardCitation = useCallback(
    async (item: ReviewItem, action: 'accept' | 'dismiss' | 'reopen') => {
      const applicabilityError = action === 'accept' && acceptErrorFor(item.securityId, item.fieldAlias, 'evidence');
      if (applicabilityError) {
        throw new Error(applicabilityError);
      }
      const citationId = item.primaryCitation?.id;
      if (citationId == null) {
        return;
      }
      await settleCitationReview({
        citationId,
        action,
        persist: capTableService.reviewCitation,
        settleMappingProposal:
          item.canSettle || action === 'reopen'
            ? undefined
            : next => settleCellMappingProposal(item.securityId, item.fieldAlias, next),
        getCell: () => findReviewCell(item.securityId, item.fieldAlias),
        getSecurities: () => latestReviewState.current.captableInfo?.securities,
        getReviewStore: useReviewStore.getState,
      });
    },
    [capTableService, findReviewCell, settleCellMappingProposal, acceptErrorFor]
  );

  const handleReviewSettleCitation = useCallback(
    async (item: ReviewItem, action: 'accept' | 'dismiss' | 'reopen') => {
      try {
        await settleCardCitation(item, action);
      } catch (error) {
        enqueueSnackbar(`Failed to ${action} the citation`, { variant: 'error' });
        throw error;
      }
      // The one card rebuild, after the citation and any Excel proposal beside it: the aggregator decides what is left.
      refreshReviewItems();
    },
    [settleCardCitation, refreshReviewItems]
  );

  // Settle an Excel card's proposal, mirror it onto the cell, the securities and the card,
  // and resolve the card's next status (pending while another proposal waits).
  const handleReviewSettleMapping = useCallback(
    async (item: ReviewItem, action: 'accept' | 'dismiss' | 'reopen'): Promise<ReviewStatus | undefined> => {
      try {
        // Read the proposal from the cell: the card's own ids lag until the next rebuild.
        const settled = await persistCellMappingProposal(item.securityId, item.fieldAlias, action, item.status);
        if (settled) {
          // Rebuild the cards from the patched cell, so the card's evidence and document names follow the reply.
          refreshReviewItems();
          return settled;
        }
        // Nothing to settle: the card takes the proposal's standing status, not the click.
        const proposal = pickMappingProposal(findReviewCell(item.securityId, item.fieldAlias)?.mappingReferences);
        return proposal && mappingRowStatus(proposal);
      } catch (error) {
        enqueueSnackbar(`Failed to ${action} the Excel mapping`, { variant: 'error' });
        throw error;
      }
    },
    [findReviewCell, persistCellMappingProposal, refreshReviewItems]
  );

  // The optional reviewer note is forwarded as `reason` (persisted as review_reason).
  // Resolves the card's next status: pending while the cell's Excel proposal still
  // waits, else accepted; the panel moves the card on that status.
  const handleReviewAccept = useCallback(
    async (
      securityId: number,
      fieldAlias: string,
      reason?: string,
      options?: { settleCitations?: boolean; settleMappingProposal?: boolean }
    ): Promise<ReviewStatus | undefined> => {
      if (fieldAlias === SECURITY_NAME_ALIAS) {
        await acceptField(securityId, fieldAlias, reason);
        settleNameOnGrid(securityId, 'accept', options);
        return undefined;
      }
      // Unresolvable select suggestion: fail BEFORE the server accept, so the
      // finding stays pending everywhere instead of settling with a lost value
      // (the throw keeps the panel card pending too).
      const unresolvedError = acceptErrorFor(securityId, fieldAlias);
      if (unresolvedError) {
        enqueueSnackbar(unresolvedError, { variant: 'error' });
        throw new Error(unresolvedError);
      }
      const sourceField = aiRequestFieldForSecurity(storeSecurity(securityId), fieldAlias);
      const request: AcceptAIFieldRequest = {
        field_names: [sourceField],
        ...(reason ? { reason } : {}),
        ...(options?.settleCitations === false ? { settle_citations: false } : {}),
      };
      let fieldError: string | undefined;
      try {
        const response = await CapTablesService.apiCapTablesSecuritiesAcceptAiFieldsPartialUpdate(
          String(securityId),
          request
        );
        fieldError = acceptAIFieldsError(response, [sourceField]);
        if (fieldError) {
          throw new Error(fieldError);
        }
      } catch (error) {
        enqueueSnackbar(fieldError ?? 'Failed to accept the suggestion', { variant: 'error' });
        throw error;
      }
      acceptFieldOnGrid(securityId, fieldAlias, options);
      if (options?.settleMappingProposal === false) {
        return 'accepted';
      }
      return (await settleFieldCardProposal(securityId, fieldAlias, 'accept')) ?? 'accepted';
    },
    [acceptField, settleNameOnGrid, acceptFieldOnGrid, acceptErrorFor, settleFieldCardProposal, storeSecurity]
  );

  // Keep the suggested value and dismiss the card's citation. The Excel source is not part of
  // this ruling: its proposal stays pending and, while it waits, so does the card.
  const handleReviewAcceptValueRejectCitation = useCallback(
    async (item: ReviewItem): Promise<ReviewStatus> => {
      // The displayed primary citation may be human-created. Select the field's
      // AI citation before accepting changes its review status.
      const cell = findReviewCell(item.securityId, item.fieldAlias);
      const citation = pickActiveCitation(
        cell?.citationReferences?.filter(ref => isAiCreatedCitation(ref) && ref.status !== 'dismissed')
      );
      await handleReviewAccept(item.securityId, item.fieldAlias, undefined, {
        settleCitations: false,
        settleMappingProposal: false,
      });
      if (citation?.id != null) {
        // The field is already accepted: settle this citation by id on both the
        // cell and store, without re-running the pending-only field patch.
        await handleReviewSettleCitation({ ...item, primaryCitation: citation }, 'dismiss');
      }
      // The proposal was not part of this ruling: while it waits, it takes the card over.
      return holdCardOnCellProposal(item.securityId, item.fieldAlias) ?? 'accepted';
    },
    [findReviewCell, handleReviewAccept, handleReviewSettleCitation, holdCardOnCellProposal]
  );

  const handleReviewDecline = useCallback(
    async (securityId: number, fieldAlias: string, reason?: string): Promise<ReviewStatus | undefined> => {
      if (fieldAlias === SECURITY_NAME_ALIAS) {
        await dismissField(securityId, fieldAlias, reason);
        return undefined;
      }
      let securityDeleted = false;
      try {
        const response = await CapTablesService.apiCapTablesSecuritiesDismissAiFieldsPartialUpdate(String(securityId), {
          field_names: [aiRequestFieldForSecurity(storeSecurity(securityId), fieldAlias)],
          ...(reason ? { reason } : {}),
        });
        securityDeleted = Boolean(response.security_deleted);
      } catch (error) {
        enqueueSnackbar('Failed to decline the suggestion', { variant: 'error' });
        throw error;
      }
      if (securityDeleted) {
        // This was the AI-created security's LAST finding: the server deleted the
        // security itself. Drop it from the store and remove its column instead of
        // patching a field on the now-gone column.
        patchSecurities(securities => securities.filter(s => s.id !== securityId));
        dismissAiColumn(securityId);
        return undefined;
      }
      dismissFieldOnGrid(securityId, fieldAlias);
      return (await settleFieldCardProposal(securityId, fieldAlias, 'dismiss')) ?? 'dismissed';
    },
    [dismissField, dismissFieldOnGrid, patchSecurities, dismissAiColumn, settleFieldCardProposal, storeSecurity]
  );

  // Reopen a card's value or citation decision: field cards through reopen-ai-fields and the
  // grid mirror, citation cards through their citation. Resolves the card's "Value kept" flag
  // and throws without a toast; handleReviewReopen reports the outcome once.
  const reopenValueOrCitation = useCallback(
    async (item: ReviewItem): Promise<{ valueKept: boolean }> => {
      if (!item.canSettle) {
        await settleCardCitation(item, 'reopen');
        return { valueKept: false };
      }
      const sourceField = aiRequestFieldForSecurity(storeSecurity(item.securityId), item.fieldAlias);
      const { reopened_fields: reopened } = await capTableService.reopenAiFields(item.securityId, [sourceField]);
      if (!reopened.includes(sourceField)) {
        throw new Error(`Field '${item.fieldAlias}' could not be reopened`);
      }
      if (item.fieldAlias === SECURITY_NAME_ALIAS) {
        // Name findings live on the COLUMN: mirror through the securities store
        // patch (grid re-parse), like the settled-name accept/decline paths. The
        // name itself is never reverted (rejecting a name rejects the column).
        patchSecurities(
          securities =>
            securities.map(s =>
              s.id === item.securityId ? applyReopenToSecurity(s, [item.fieldAlias], securities) : s
            ) as SecurityModel[]
        );
        return { valueKept: false };
      }
      return reopenFieldOnGrid(item.securityId, item.fieldAlias, item.status === 'accepted');
    },
    [settleCardCitation, capTableService, patchSecurities, reopenFieldOnGrid, storeSecurity]
  );

  // Undo a settled card: its Excel proposal first, then its value or citation, then one card rebuild. A failed
  // proposal call leaves the whole card settled; a failure after it gets one warning naming the partial result.
  const handleReviewReopen = useCallback(
    async (item: ReviewItem): Promise<{ valueKept: boolean }> => {
      let proposalReopened = false;
      // A security-name finding settles its column, never an Excel reference.
      if (!(item.canSettle && item.fieldAlias === SECURITY_NAME_ALIAS)) {
        try {
          proposalReopened =
            (await persistCellMappingProposal(item.securityId, item.fieldAlias, 'reopen', item.status)) !== undefined;
        } catch (error) {
          enqueueSnackbar('Failed to reopen the Excel mapping', { variant: 'error' });
          throw error;
        }
      }
      try {
        return await reopenValueOrCitation(item);
      } catch (error) {
        if (!proposalReopened) {
          enqueueSnackbar(item.canSettle ? 'Failed to reopen the review item' : 'Failed to reopen the citation', {
            variant: 'error',
          });
          throw error;
        }
        enqueueSnackbar('Reopened the Excel mapping only; the rest of this card stays as it was', {
          variant: 'warning',
        });
        return { valueKept: false };
      } finally {
        if (proposalReopened) {
          refreshReviewItems();
        }
      }
    },
    [persistCellMappingProposal, reopenValueOrCitation, refreshReviewItems]
  );

  // Decide the selected cards in one review batch, then rebuild the cards once before answering.
  const handleReviewBulkSettle = useCallback(
    async (action: ReviewBulkAction, items: ReviewItem[], reason?: string): Promise<ReviewBulkResult> => {
      // Taken before the request: a reply for a table the analyst has left must change nothing.
      const stillCurrent = guardSelection();
      const capTableId = captableInfo?.id;
      // Reloads the table this call started on with one toast saying why and whether unsaved edits go with it,
      // read when the reload starts. It never rejects: the panel would put back cards the server settled.
      const reload = async (message: string, variant: VariantType) => {
        if (capTableId == null || !stillCurrent()) {
          return;
        }
        const { dirtyBySecurityId } = useCapTableDirtyCellStore.getState();
        const editsLost =
          Boolean(latestReviewState.current.unsavedChanges) || Object.keys(dirtyBySecurityId).length > 0;
        enqueueSnackbar(`${message}${editsLost ? ', and your unsaved edits were discarded' : ''}`, { variant });
        await refreshCapTable(capTableId, stillCurrent).catch(error =>
          Logger.error('CapTableWidgetDrawer - cap table reload after a bulk review failed', error)
        );
      };
      // A suggestion the grid cannot apply (an option name matching nothing) never reaches the server.
      const { acceptable, failures: unresolved } =
        action === 'accept'
          ? partitionAcceptableItems(items, item =>
            acceptErrorFor(item.securityId, item.fieldAlias, item.canSettle ? 'value' : 'evidence')
          )
          : { acceptable: items, failures: [] as BulkFailure[] };
      // The proposal comes from the cell: a card's own ids lag until the next rebuild.
      const entries = buildReviewBatch(
        acceptable,
        action,
        card => findReviewCell(card.securityId, card.fieldAlias)?.mappingReferences,
        card => aiRequestFieldForSecurity(storeSecurity(card.securityId), card.fieldAlias)
      );
      let response: ReviewBatchResponse | undefined;
      if (capTableId != null && entries.length > 0) {
        try {
          response = await capTableService.reviewBatch(capTableId, {
            action,
            items: entries.map(entry => entry.item),
            ...(reason ? { reason } : {}),
          });
        } catch (error) {
          Logger.error('CapTableWidgetDrawer - bulk review failed', error);
          if (stillCurrent()) {
            // The panel moved the cards optimistically; put every card back on what its cell holds.
            refreshReviewItems();
            // A 400 means nothing ran because the page is stale: an id it holds is no longer linked on the server.
            if ((error as { status?: number } | null)?.status === 400) {
              await reload('The cap table changed since it was loaded, so nothing was saved; reloading it', 'error');
            } else {
              enqueueSnackbar(extractResponseErrorMessage(error) ?? `Failed to ${action} the selected items`, {
                variant: 'error',
              });
            }
          }
          return { settledIds: [] };
        }
      }
      if (!stillCurrent()) {
        return { settledIds: [] };
      }

      const { settled, failed } = pairBatchResults(entries, response?.results ?? []);
      const failures = [...unresolved, ...failed.map(({ entry, message }) => ({ card: entry.card, reason: message }))];
      let settledIds = settled.map(({ entry }) => entry.card.id);
      const valueKeptIds: string[] = [];
      let mirrored = true;
      try {
        const evidenceAction = evidenceActionOf(action);
        const storeSecurities = latestReviewState.current.captableInfo?.securities;
        settled.forEach(({ entry: { card }, result }) => {
          const cell = findReviewCell(card.securityId, card.fieldAlias);
          // The result echoes the key the request sent; the decision lands on the card's own cell.
          (result.settled_fields.length > 0 ? [card.fieldAlias] : []).forEach(field => {
            if (field === SECURITY_NAME_ALIAS) {
              // The cell shows the decision now, so the card rebuild before the grid re-parse never shows it pending.
              settleNameOnGrid(card.securityId, action);
            } else if (action === 'accept') {
              acceptFieldOnGrid(card.securityId, field);
            } else if (action === 'decline') {
              dismissFieldOnGrid(card.securityId, field);
            } else if (reopenFieldOnGrid(card.securityId, field, card.status === 'accepted').valueKept) {
              valueKeptIds.push(card.id);
            }
          });
          result.citations.forEach(({ id }) => {
            applyCitationReviewToSecurities(storeSecurities, id, evidenceAction);
            applyCitationReviewToCell(cell, id, evidenceAction);
          });
          if (result.mappings.length > 0 || result.restored.length > 0) {
            const change = mappingChangeOf(result, action);
            const wasGreen = cellHasReferenceGreen(cell);
            applyMappingReviewToCell(cell, change);
            applyMappingReviewToSecurities(storeSecurities, change);
            repaintLostGreen(cell, wasGreen);
          }
        });
        // A settled name or a deleted security is the one case that replaces the store (a grid rebuild). It runs
        // after every in-place mirror and carries every settled field, so the rebuild brings nothing back as pending.
        const fieldsBySecurity = settledFieldsBySecurity(settled);
        const hasName = Array.from(fieldsBySecurity.values()).some(fields => fields.includes(SECURITY_NAME_ALIAS));
        const deleted = new Set(response?.deleted_security_ids ?? []);
        if (hasName || Array.from(fieldsBySecurity.values()).some(fields => fields.includes('options_ledger')) || deleted.size > 0) {
          patchSecurities(current =>
            (applySettledFieldsToSecurities(current, fieldsBySecurity, action) as SecurityModel[]).filter(
              security => security.id == null || !deleted.has(security.id)
            )
          );
          deleted.forEach(dismissAiColumn);
        }
        // The one card rebuild; a card it still shows pending, held by another Excel proposal, is not settled.
        const rebuilt = refreshReviewItems();
        if (action !== 'reopen') {
          const stillPending = new Set(rebuilt.filter(card => card.status === 'pending').map(card => card.id));
          settledIds = settledIds.filter(id => !stillPending.has(id));
        }
      } catch (error) {
        // The server already holds these decisions: keep them, and reload below so the page shows them.
        Logger.error('CapTableWidgetDrawer - mirroring the bulk review reply failed', error);
        mirrored = false;
      }
      if (failures.length > 0) {
        enqueueSnackbar(bulkFailureSummary(action, failures), { variant: 'warning' });
        Logger.warn('CapTableWidgetDrawer - bulk review partial failure', failures.map(failure => failure.reason));
      }
      if (!mirrored) {
        await reload('Saved the decisions, but the page could not show them; reloading the cap table', 'warning');
      } else if ((response?.restored_security_ids ?? []).length > 0) {
        // A reopen brought back a column this page had already dropped.
        await reload('A security came back with this reopen; reloading the cap table', 'info');
      }
      return { settledIds, valueKeptIds };
    },
    [
      guardSelection,
      storeSecurity,
      captableInfo?.id,
      refreshCapTable,
      acceptErrorFor,
      findReviewCell,
      capTableService,
      refreshReviewItems,
      acceptFieldOnGrid,
      dismissFieldOnGrid,
      reopenFieldOnGrid,
      settleNameOnGrid,
      repaintLostGreen,
      patchSecurities,
      dismissAiColumn,
    ]
  );

  // Accept or reject the selection's pending suggestions through the review panel's bulk path. A card with a decision
  // still saving sits this batch out, and the batch's own cards stay busy until it answers.
  const handleGridSelectionSettle = (action: 'accept' | 'decline') =>
    runGridSelectionAction(async () => {
      const [verb, done] = action === 'accept' ? ['accept', 'Accepted'] : ['reject', 'Rejected'];
      const { reviewItems: storeItems, busyCardIds, holdCards } = useReviewStore.getState();
      const pending = pendingReviewItemsForSelection(
        selectedCapTableCells(),
        storeItems.length > 0 ? storeItems : refreshReviewItems()
      );
      const items = pending.filter(item => !busyCardIds.has(item.id));
      if (items.length === 0) {
        const message =
          pending.length > 0 ? 'The selected suggestions are still saving' : `No pending suggestions to ${verb}`;
        enqueueSnackbar(message, { variant: 'info' });
        return;
      }
      const release = holdCards(items.map(item => item.id));
      const { settledIds } = await handleReviewBulkSettle(action, items).finally(release);
      if (settledIds.length > 0) {
        enqueueSnackbar(`${done} suggestions on ${settledIds.length} cell${settledIds.length === 1 ? '' : 's'}`, {
          variant: 'success',
        });
      }
    });

  // SCAL-8976 (Feature B): the card's eyeball icon reveals its PRIMARY source
  // citation in the PDF viewer, highlighted by the citation's REVIEW STATUS
  // (pending -> AI-purple outline, reviewed -> plain yellow; SCAL-8986). Mirrors
  // CitationValueViewer.onView (setActiveCitation + NAVIGATE_TO_DOCUMENT_REFERENCE).
  // NO-OP for items with no document-backed citation.
  const handleReviewRevealPdf = useCallback(
    (item: ReviewItem) => {
      const jump = buildReviewDocJump(item);
      if (!jump) {
        return;
      }
      setActiveCitation(jump.activeCitation, jump.variant);
      publish(EventType.NAVIGATE_TO_DOCUMENT_REFERENCE, jump.navigatePayload);
    },
    [setActiveCitation, publish]
  );

  // SCAL-8976 (Feature C): a review card body becoming active reveals its PRIMARY
  // Excel mapping-reference source - open the xlsx viewer to the mapped sheet and
  // highlight the mapped cell(s) via NAVIGATE_TO_DOCUMENT_REFERENCE. Unlike the
  // eyeball PDF jump it does NOT setActiveCitation. NO-OP for items with no Excel
  // mapping reference (buildReviewExcelJump returns null).
  const handleReviewRevealExcel = useCallback(
    (item: ReviewItem) => {
      const jump = buildReviewExcelJump(item);
      if (!jump) {
        return;
      }
      publish(EventType.NAVIGATE_TO_DOCUMENT_REFERENCE, jump.navigatePayload);
    },
    [publish]
  );

  // Drops the Excel references a workbook clear retired and puts back the ones it restored; citations and AI cards
  // stay. The reply can land after a grid rebuild, so it patches the latest cells.
  const clearCellReferences = (result: Partial<ClearEvidenceResult>, docRefPayload?: ClearAttributeMappingsPayload) => {
    const cells = latestReviewState.current.workbook?.captable?.cells as
      | Record<string, CapTableCellWithReferences>
      | undefined;
    // A table still reloading has no cells to patch; its load brings the cleared references.
    if (!cells) {
      return;
    }
    const retiredIds = new Set(result.retired_mapping_ids ?? []);
    const inScope = (ref: MappingReferenceData) =>
      !docRefPayload ||
      (ref.document_reference === docRefPayload.documentReferenceId &&
        (!docRefPayload.sheet || ref.sheet_name === docRefPayload.sheet));
    // Saved rows go when the server retired them; unsaved picks go when they fall in the cleared scope.
    const isCleared = (ref: MappingReferenceData) => (ref.id != null ? retiredIds.has(ref.id) : inScope(ref));

    const changed: CapTableCellWithReferences[] = [];
    Object.values(cells).forEach(cell => {
      const mappings = cell.mappingReferences ?? [];
      const kept = mappings.filter(ref => !isCleared(ref));
      // A cleared accepted reference brings back what it replaced outside the scope, unless edited off this cell.
      const next = replaceMappingRows(
        kept,
        [],
        restoredRowsOnCell(result.restored_mappings ?? [], cell, storeSecurity(Number(cell.columnId)))
      ) ?? kept;
      // "Clear all" also drops the column-level document references.
      const dropsColumnar =
        !docRefPayload && Boolean(cell.columnarReferences?.length || cell.columnarReferencesMetadata?.length);
      if (next === kept && kept.length === mappings.length && !dropsColumnar) {
        return;
      }

      cell.mappingReferences = next;
      if (!next.length) {
        cell.columnarReferences = [];
        cell.columnarReferencesMetadata = [];
      }
      syncReferenceGreen(cell);
      changed.push(cell);
    });
    // Programmatic repaints, so the value-change handler does not strip the cells' citations and AI cards.
    changed.forEach(cell => repaintCell(cell));

    // A changed column recounts the documents its cells reference, restored rows included.
    if (docRefPayload && changed.length > 0) {
      const referenceGeneratorService = new CapTableReferenceGenerator();
      const affectedColumns = new Set(changed.flatMap(({ columnLegend }) => (columnLegend ? [columnLegend] : [])));
      affectedColumns.forEach(columnLegend => {
        const capCell = cells[`${columnLegend}1`];
        if (capCell) {
          const columnMappingReferences = Object.values(cells)
            .filter(cell => cell.columnLegend === columnLegend && cell !== capCell)
            .flatMap(cell => cell.mappingReferences ?? []);
          const docRefsData = referenceGeneratorService.getDocumentReferences(columnMappingReferences);
          capCell.columnarReferences = docRefsData.documentIds;
          capCell.columnarReferencesMetadata = docRefsData.metadata;
          capCell.dispatchEvent();
        }
      });
    }
  };

  const captableIndex = (alias: string) =>
    rowConfig({
      isDisabled: false,
      measurementDate: '',
      customTitles: {},
      firmName: '',
    }).findIndex(item => item.alias === alias);

  // Saves one Excel pick on a saved security after any earlier save on its cell; rejects when the save fails.
  const saveExcelPick = ({ cell, pick, value }: ExcelPick, stillCurrent: () => boolean) => {
    const securityId = Number(cell.columnId);
    const alias = String(cell.alias);
    return queueCellSave(securityId, alias, async () => {
      const [outcome] = await Promise.allSettled([
        capTableService.createSecurityMapping(securityId, {
          field_name: alias,
          document_reference: pick.document_reference,
          sheet_name: pick.sheet_name,
          cell_references: pick.cell_references,
        }),
      ]);
      // A reply that lands after the analyst moved to another table must not touch the new one.
      if (stillCurrent()) {
        const live = findReviewCell(securityId, alias);
        if (outcome.status === 'fulfilled') {
          writeCreatedMapping(live, storeSecurity(securityId), outcome.value);
        }
        if (live) {
          // A value the pick does not back (its save failed, or the analyst typed meanwhile) drops its editable
          // references for Save to retire.
          if ((outcome.status === 'rejected' || !sameAiValue(live.value, value)) && stripEditableMappings(live)) {
            syncReferenceGreen(live);
          }
          repaintCell(live);
        }
      }
      if (outcome.status === 'rejected') {
        throw outcome.reason;
      }
    });
  };

  // Saves the Excel picks of one push, then reports each distinct failure and rebuilds the review cards once;
  // true when the push is still current and not every pick failed.
  const saveExcelPicks = async (picks: ExcelPick[]) => {
    if (picks.length === 0) {
      return true;
    }
    const stillCurrent = guardSelection();
    const results = await Promise.allSettled(picks.map(pick => saveExcelPick(pick, stillCurrent)));
    // A reply that lands after the analyst moved to another table reports nothing.
    if (!stillCurrent()) {
      return false;
    }
    toastDistinctFailures(results, index => {
      const { alias, key } = picks[index].cell;
      return `Failed to save the Excel reference on ${alias}(${key})`;
    });
    refreshCellEvidence();
    return results.some(result => result.status === 'fulfilled');
  };

  const pushAttributeMappingsToCapTable = async (payload?: HandlePushingAttributeMappingsPayload) => {
    const mapper = getAttributeMapperManager();
    if (!mapper) {
      return;
    }

    // Picks on saved securities are saved after the value writes, once the programmatic flag is off again.
    const picks: ExcelPick[] = [];
    // Rows an unsaved column's pick took off must not leave review cards behind.
    let unsavedPickDroppedRows = false;
    captableSetProgrammaticUpdate?.(true);

    try {
      const securityFields = rowConfig({ isDisabled: false, measurementDate: '', customTitles: {} }).map(
        item => item.alias
      );
      const mappingService = new CapTableReferenceGenerator(securityFields);

      // find which cell is in an expr state
      const cellsInExprState = Object.values(workbook.captable.cells as CapTableCellWithReferences[]).filter(
        cell => cell.expr === '='
      );

      if (!cellsInExprState.length) {
        return;
      }

      if (!payload?.documentReferencePayload) {
        return;
      }

      cellsInExprState.forEach(cell => {
        const titleCellForColumn = workbook.captable.cells[`${cell.columnLegend}1`] as CapTableCellWithReferences;

        // need to clear similar xValue and yValue pairs from the mapper
        const expectedXValue = cell.alias;
        const expectedYValue = titleCellForColumn.value;

        // for cap table each single cell selected in the table must only be represented by one value below
        mapper.removeSingleCellByXYValue({
          xValue: expectedXValue,
          yValue: expectedYValue,
        });

        mapper.handleSingularAddition(payload.documentReferencePayload!, {
          // coordinates should be the cell in the drawer sheet
          coordinates: `${payload.selectedCell?.cellInfo.key}`,

          // these are the source values
          xValue: cell.alias,
          yValue: titleCellForColumn.value,
          direction: 'positive',
        });
      });

      const mapping = getCellMapping();
      const evaluatedAttributeData = mapper.calculateValues(mapping);

      // Get the sheet attribute mapping which contains the reference data
      const { sheetAttributeMapping } = mapper;

      // Generate mapping references from the sheet attribute data (not evaluated data)
      const generatedReferences = mappingService.generateMappingReferences(sheetAttributeMapping.data);

      const captableChangeSet: CellChange[] = [];

      Object.keys(evaluatedAttributeData).forEach(columnarMetric => {
        Object.keys(evaluatedAttributeData[columnarMetric]).forEach(rowMetric => {
          const captableRowIndex = captableIndex(rowMetric);

          if (captableRowIndex >= 0) {
            const updatedCell = Object.values(workbook.captable.cells as CapTableCellWithReferences[]).find(
              (item: CapTableCellWithReferences) => {
                // Only consider cells that are in mapping mode (expr === '=')
                if (item.expr !== '=') {
                  return false;
                }

                const titleColumn = workbook.captable.cells[`${item.columnLegend}1`];
                if (!titleColumn) {
                  return false;
                }

                return item.alias === rowMetric && titleColumn.value === columnarMetric;
              }
            ) as CapTableCellWithReferences | undefined;
            if (updatedCell) {
              const mappedValue = evaluatedAttributeData[columnarMetric][rowMetric].value;

              // // Attach reference metadata to the cell
              const referenceMetadata = mappingService.getReferenceMetadataForCell(
                columnarMetric,
                rowMetric,
                generatedReferences
              );

              // // Add mapping reference metadata to cell
              const titleCell = workbook.captable.cells[`${updatedCell.columnLegend}1`];

              if (referenceMetadata) {
                // A saved security saves the pick at once; an unsaved column keeps it on the cell until Save.
                if (isSavedSecurityId(updatedCell.columnId)) {
                  // The value write below is programmatic, so record it as unsaved: a rebuild then keeps it.
                  useCapTableDirtyCellStore
                    .getState()
                    .recordDirty(Number(updatedCell.columnId), String(updatedCell.alias), mappedValue);
                  picks.push({ cell: updatedCell, pick: referenceMetadata[0], value: mappedValue });
                } else {
                  unsavedPickDroppedRows = placeUnsavedPick(updatedCell, referenceMetadata) || unsavedPickDroppedRows;
                }
                const columnarRefs = mappingService.getDocumentReferences(referenceMetadata);

                // set col refs on column
                titleCell.columnarReferences = columnarRefs.documentIds;
                titleCell.columnarReferencesMetadata = columnarRefs.metadata;

                captableChangeSet.push({
                  cell: titleCell,
                  value: titleCell.value,
                });
              } else {
                // No reference behind the value: the editable references go (Save retires them); AI proposals stay.
                stripEditableMappings(updatedCell);
                syncReferenceGreen(updatedCell);
                titleCell.columnarReferences = [];
                titleCell.columnarReferencesMetadata = [];
              }

              // here add in the cell refs for the col

              captableChangeSet.push({
                cell: updatedCell,
                value: mappedValue,
              });
            }
          }
        });
      });
      if (unsavedPickDroppedRows) {
        refreshCellEvidence();
      }

      const fullChanges = captableChangeSet;
      fullChanges.forEach(change => {
        onTableChange(change.cell, change.value);
      });
    } finally {
      // Reset flag after all changes are complete
      captableSetProgrammaticUpdate?.(false);
    }
    // The push reports success once its picks are saved, and not when every one of them failed.
    if ((await saveExcelPicks(picks)) && payload?.showNotification) {
      enqueueSnackbar('Mapping pushed to captable', { variant: 'success' });
    }
  };

  // Workbook-toolbar clear: the server retires every Excel reference in scope, then the grid follows.
  const handleClearMappings = async (docRefPayload?: ClearAttributeMappingsPayload) => {
    const capTableId = captableInfo?.id;
    if (!capTableId) {
      enqueueSnackbar('Cap table is not loaded', { variant: 'error' });
      return;
    }
    const stillCurrent = guardSelection();
    try {
      // Sends only the scope the toolbar picked; no document clears the whole cap table.
      const result = await capTableService.clearMappings(capTableId, {
        ...(docRefPayload ? { document_id: docRefPayload.documentReferenceId } : {}),
        ...(docRefPayload?.sheet ? { sheet_name: docRefPayload.sheet } : {}),
      });
      // A reply that lands after the analyst moved to another table must not touch the new one.
      if (!stillCurrent()) {
        return;
      }
      clearCellReferences(result, docRefPayload);
      writeClearToStore(result);
      clearAttributeMapperManager(docRefPayload);
      refreshCellEvidence();
    } catch (error) {
      Logger.error('CapTableWidgetDrawer - clear mappings failed', error);
      enqueueSnackbar(extractResponseErrorMessage(error) ?? 'Failed to clear the mappings', { variant: 'error' });
    }
  };

  const filterRows = row => !row.readOnly;
  const mapRowsToOptions = row => ({ label: row.value, value: row.alias });

  const attributeReferenceConfig = useMemo(() => {
    if (!captableInfo) {
      return { columns: [], rows: [], selectionMode: AttributeMappingType.SINGLE_CELL };
    }

    const columns = captableInfo.securities?.map((security: SecurityModel) => ({
      label: security.name,
      value: security.id,
    }));

    const captableRows = rowConfig({
      isDisabled: false,
      measurementDate: '',
      customTitles: {},
      firmName: '',
    });
    const captableOptions = captableRows.filter(filterRows).map(mapRowsToOptions);
    const rows = [{ label: 'CapTable', value: null, isTitle: true }, ...captableOptions];
    return {
      columns,
      rows,
      selectionMode: AttributeMappingType.SINGLE_CELL,
    };
  }, [captableInfo]);

  const handleCitationPushed = (payload: CitationPushedPayload, documentReferenceId: number) => {
    setCitation({ ...payload, documentReferenceId });
    const selectMultipleAction = isAiReviewer
      ? () => (
        <Button
          color="inherit"
          size="small"
          onClick={() => useGridCitationSelectionStore.getState().enterAddMultiple()}>
            Select multiple
        </Button>
      )
      : undefined;
    enqueueSnackbar('Click a cell to add this citation', { action: selectMultipleAction });
  };

  const handleReprocessClick = useCallback(
    (documentClicked: DocumentReferencesView) => {
      setReprocessJobMetadata({
        document_reference_id: documentClicked.document_reference_id,
        measurement_date_id: currentMeasurementDate?.id,
        file_id: documentClicked.file.id,
      });
      setReprocessDialogOpen(true);
    },
    [currentMeasurementDate?.id]
  );

  const handleReprocessCancel = () => {
    setReprocessJobMetadata(null);
    setReprocessDialogOpen(false);
  };

  const handleReprocessConfirm = async () => {
    setReprocessDialogOpen(false);
    if (!reprocessJobMetadata || reprocessJobMetadata.file_id == null) {
      enqueueSnackbar('No document information available to reprocess', { variant: 'error' });
      return;
    }
    if (!companyInfo?.id) {
      enqueueSnackbar('Company information not available', { variant: 'error' });
      return;
    }

    const companyIdStr = companyInfo.id.toString();
    const fileId = reprocessJobMetadata.file_id;

    try {
      const newJob = await ApiService.apiCompanyDocumentGenerationJobsNewCreate(companyIdStr, {
        processing_type: ApiCreateDocumentGenerationJob.processing_type.CAP_TABLE,
        reference_files: [fileId.toString()] as any,
        desired_termination_state: 'COMPLETE',
        parser_overrides: [
          {
            file_type: 'xlsx',
            xlsx_parsing_mode: 'JSON_CONVERSION',
            minify_json: true,
          } as any,
        ],
        metadata: {
          document_reference_id: reprocessJobMetadata.document_reference_id,
          measurement_date_id: reprocessJobMetadata.measurement_date_id,
          file_id: fileId,
        },
      } as any);

      Logger.info('CapTableWidgetDrawer:', `Adding cap table job ${newJob.job_id} to polling`);

      addJob({
        id: newJob.job_id,
        companyId: companyIdStr,
        details: {
          title: CAP_TABLE_REPROCESS_JOB_TITLE,
          description: `Reprocessing document ${reprocessJobMetadata.document_reference_id}`,
        },
        pollingConfig: {
          pollingFunction: async () => CompanyService.apiCompanyDocumentGenerationJobsRead(companyIdStr, newJob.job_id),
          pollingInterval: 3000,
          pollingArgs: [],
        },
        // Same terminal handling as the Process flow: apply, announce the count or
        // warn honestly when nothing was extracted, stay silent on already_applied.
        // The grid refresh rides the published completion event, consumed by the
        // cap-table page root (useCapTableGenerationRefresh) so it fires no matter
        // which drawer tab is active.
        onResult: createCapTableGenerationOnResult({
          companyId: companyIdStr,
          logContext: 'CapTableWidgetDrawer',
          publish,
          removeJob,
        }),
        onError: (error: Error) => {
          Logger.error('CapTableWidgetDrawer - Reprocess job polling error', error);
        },
      });

      enqueueSnackbar('Reprocessing started. The cap table will update when the job completes.', {
        variant: 'info',
      });
    } catch (error: any) {
      Logger.error('CapTableWidgetDrawer - Error reprocessing document', error);
      const errorMessage = error?.body?.detail || error?.message || 'Failed to reprocess document';
      enqueueSnackbar(`Error: ${errorMessage}`, { variant: 'error' });
    } finally {
      setReprocessJobMetadata(null);
    }
  };

  // Names the table in the toast: a slow refresh must never leave which one was cleared in doubt.
  const selectedCapTableLabel = [
    companyInfo?.name,
    captableInfo?.is_primary === false ? captableInfo?.name : null,
    currentMeasurementDate?.name,
  ]
    .filter(Boolean)
    .join(' - ');

  // The clear actions match the server: staff with edit rights on this company, or a superuser.
  const canClearCapTable = useCanClearCapTable(companyInfo?.id);

  const handleConfirmReset = async (mode: CapTableResetMode) => {
    const capTableId = captableInfo?.id;
    const stillCurrent = guardSelection();
    if (!capTableId) {
      enqueueSnackbar('Cap table is not loaded', { variant: 'error' });
      return false;
    }
    try {
      const result = await CapTablesService.apiCapTablesAiEvidenceResetCreate(String(capTableId), {
        mode: CAP_TABLE_RESET_API_MODE[mode] as AIEvidenceResetRequest.mode,
      });
      await refreshCapTable(capTableId, stillCurrent);
      enqueueSnackbar(capTableResetSummary(mode, result), { variant: 'success' });
      return true;
    } catch (error) {
      Logger.error('CapTableWidgetDrawer - reset failed', error);
      enqueueSnackbar(extractResponseErrorMessage(error) ?? 'Failed to complete the reset', { variant: 'error' });
      return false;
    }
  };

  const handleClearCapTable = async (targetCapTableId: number, targetLabel: string) => {
    const stillCurrent = guardSelection();
    await runCapTableClear(targetCapTableId, {
      clearCapTable: id => capTableService.clearCapTable(id),
      refresh: () => refreshCapTable(targetCapTableId, stillCurrent),
      notify: (message, variant) => enqueueSnackbar(message, { variant }),
      targetLabel,
      isStillSelected: stillCurrent,
    });
  };

  const canSettleSelection = gridSelectionMode === 'add-multiple' && selectedCapTableCells().some(cellHasPendingSuggestion);

  return (
    <>
      {isAiReviewer &&
        gridSelectionMode === 'add-multiple' &&
        gridSelectionToolbarHost &&
        createPortal(
          <GridCitationSelectionToolbar
            count={gridSelected.length}
            canSettle={canSettleSelection}
            hasStagedCitation={Boolean(citation)}
            busy={gridCitationBusy}
            onAttach={handleGridSelectionAttach}
            onSettle={handleGridSelectionSettle}
            onClear={canClearCapTable ? handleGridSelectionClear : undefined}
            onFilterDrawer={handleGridSelectionFilter}
            onCancel={handleGridSelectionCancel}
          />,
          gridSelectionToolbarHost
        )}
      <AttributeMappedWidgetDrawer
        documentReferenceHeaderContent={
          <GenerateCapTableJob
            trigger={
              <AiProcessingMenu
                onGenerateCapTable={() => setGenerateDialogOpen(true)}
                onCreateCitations={createCitationsAction.isAvailable ? createCitationsAction.openDialog : undefined}
                createCitationsDisabledReason={createCitationsAction.disabledReason}
                onConfirmReset={handleConfirmReset}
                canClear={canClearCapTable}
                capTableId={selectedCapTableId}
                capTableLabel={selectedCapTableLabel}
                onClearCapTable={handleClearCapTable}
                onMenuOpenChange={open => {
                  if (open) {
                    setCreateCitationsEnabled(true);
                  }
                }}
              />
            }
            dialogOpen={generateDialogOpen}
            onDialogOpenChange={setGenerateDialogOpen}
            selectedMeasurementDate={drawerSelection?.selectedMeasurementDate}
            currentPage={drawerSelection?.currentPage}
            referencedFeatureId={drawerSelection?.referencedFeatureId}
          />
        }
        documentApplicationStorageKey={documentApplicationStorageKey}
        attributeMapper={attributeMapperManager}
        attributeReferenceConfig={attributeReferenceConfig}
        onCitationPushed={handleCitationPushed}
        onReprocessDocument={handleReprocessClick}
        applicationContext={applicationContext}
        documentReferencesProps={documentReferencesProps}
        notesProps={notesProps}
        spreadsheetNotesProps={spreadsheetNotesProps}
        handlePushingAttributeMappings={pushAttributeMappingsToCapTable}
        handleClearMappings={handleClearMappings}
        sidePanel={
          !isAiReviewer || reviewPaneCollapsed ? undefined : (
            <ReviewSidePanel
              onAccept={handleReviewAccept}
              onDecline={handleReviewDecline}
              onAcceptValueRejectCitation={handleReviewAcceptValueRejectCitation}
              onSettleCitation={handleReviewSettleCitation}
              onSettleMapping={handleReviewSettleMapping}
              onReopen={handleReviewReopen}
              onBulkSettle={handleReviewBulkSettle}
              onRevealPdf={handleReviewRevealPdf}
              onRevealExcel={handleReviewRevealExcel}
              documentNamesByRefId={documentNamesByRefId}
            />
          )
        }
        headerLeading={isAiReviewer ? <PendingReviewBadge /> : undefined}
      />
      <CreateCapTableCitationsDialog
        documents={createCitationsAction.documents}
        onClose={createCitationsAction.closeDialog}
        open={createCitationsAction.isDialogOpen}
      />

      {/* Keeping both is the default; replacing the AI citation must be an explicit choice. Cancel keeps the
          citation staged for another cell. */}
      <Dialog open={replacePromptCell !== null} onClose={() => setReplacePromptCell(null)}>
        <DialogTitle>Cell already has an AI citation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This cell already has an AI-generated citation. Keep both citations, or replace the AI citation with your
            manual one? Replacing dismisses the AI citation.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplacePromptCell(null)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => replacePromptCell && completeManualCitationAdd(replacePromptCell, 'replace')}
            color="inherit">
            Replace AI citation
          </Button>
          <Button
            onClick={() => replacePromptCell && completeManualCitationAdd(replacePromptCell, 'append')}
            color="primary"
            variant="contained"
            autoFocus>
            Keep both
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reprocessDialogOpen} onClose={handleReprocessCancel}>
        <DialogTitle>Reprocess Cap Table Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will run a new AI cap table processing job on this document. When it completes, the AI results will be
            applied to the cap table and existing securities will be replaced.
            <br />
            <br />
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReprocessCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={() => handleReprocessConfirm()} color="primary" variant="contained">
            Reprocess
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CapTableWidgetDrawer;
